import {Dispatch, useCallback, useEffect, useRef, useState} from "react";
import {useMatch} from "react-router-dom";
import {useDispatch} from "react-redux";
import {projectsApi} from "../../api/projectsApi";
import {isErrorResponse} from "../../utils/utils";
import {useTypedSelector} from "../../redux/Hooks/storeSelectors";
import {selectCurrentProjectDefaultMode, selectCurrentProjectModes} from "../../redux/selectors/selectors";
import {addedProjectModes, AllowedProjectModes, DEFAULT_ALLOWED_MODES} from "../../redux/reducers/projectsReducer";
import {useToastContext} from "../../context/toastContext";
import {useTranslation} from "react-i18next";
import {ApiError} from "../../api/RestClient";

export type ModePath =
    | 'project'
    | 'layers'
    | 'klayers'
    // | 'algorithms'
    | 'dashboard'
    | 'network'
    | 'transit'
    | 'maas'
    | 'config'
    | 'runs'
    | 'ev'
    | 'scenario'
    | 'logistics';

export const MAPPED_URL_TO_MODE: Record<ModePath, keyof AllowedProjectModes> = {
    'project': 'projectPage',
    'layers': 'gisLayers',
    'klayers': 'gisV2Layers',
    // 'algorithms': 'runOptimization',
    'dashboard': 'biDashboard',
    'network': 'networkEditor',
    'transit': 'transitScheduleEditor',
    'maas': 'sharedMobilityEditor',
    'config': 'advancedConfig',
    'runs': 'runs',
    'ev': 'evInfrastructureEditor',
    'scenario': 'scenarioPresetEditor',
    "logistics": 'dvInfrastructureEditor'
};

export const MAPPED_MODE_TO_MODEPATH: Record<keyof AllowedProjectModes, ModePath> = {
    'projectPage': 'project',
    'gisLayers': 'layers',
    'gisV2Layers': 'klayers',
    // 'runOptimization': 'algorithms',
    'biDashboard': 'dashboard',
    'networkEditor': 'network',
    'transitScheduleEditor': 'transit',
    'sharedMobilityEditor': 'maas',
    'advancedConfig': 'config',
    'runs': 'runs',
    'evInfrastructureEditor': 'ev',
    'scenarioPresetEditor': 'scenario',
    'dvInfrastructureEditor': 'logistics'
};

export type UseAllowedModes = {
    projectModesLoaded: boolean;
    setProjectModesLoaded: Dispatch<boolean>
    modesError?: ApiError | null;
    allowedModes: AllowedProjectModes;
    mode: ModePath;
    isModeAllowed: boolean;
    loadProjectModes: (projectId: string) => Promise<void>;
};

export const useAllowedModes = (): UseAllowedModes => {
    const match = useMatch('/:projectId/:mode/*');
    const [mode, setMode] = useState<ModePath>('project');
    const [isModeAllowed, setIfModeAllowed] = useState<boolean>(true);
    const [projectModesLoaded, setProjectModesLoaded] = useState<boolean>(false);
    const [modesError, setError] = useState<ApiError | null>(null);
    const allowedModes = useTypedSelector(selectCurrentProjectModes);
    const defaultMode = useTypedSelector(selectCurrentProjectDefaultMode);
    const abortControllerRef = useRef(new AbortController());
    const dispatch = useDispatch();
    const {addToast} = useToastContext();
    const {t} = useTranslation();

    const checkIfAllowedAndSetMode = useCallback(({mode, defaultMode, availableModes}: {
        mode?: string;
        defaultMode: ModePath;
        availableModes: AllowedProjectModes;
    }) => {
        const isModeAllowed = mode && mode in MAPPED_URL_TO_MODE && (availableModes[MAPPED_URL_TO_MODE[mode]] || DEFAULT_ALLOWED_MODES[MAPPED_URL_TO_MODE[mode]]);

        setMode(isModeAllowed ? mode as ModePath : defaultMode ? MAPPED_MODE_TO_MODEPATH[defaultMode] : '.');
        setIfModeAllowed(isModeAllowed);
    }, [])

    //TODO add loaded flag to redux and load once
    const loadAllowedModes = useCallback(async (projectId: string) => {
        const resp = await projectsApi.getAllowedModes(projectId, abortControllerRef.current.signal);

        let defaultMode: ModePath = 'project';
        if (!isErrorResponse(resp)) {
            const {
                projectPage,
                runOptimization,
                biDashboard,
                sharedMobilityEditor,
                networkEditor,
                transitScheduleEditor,
                advancedConfig,
                runs,
                evInfrastructureEditor,
                scenarioPresetEditor,
                dvInfrastructureEditor,
                gisLayers,
                gisV2Layers
            } = resp;

            if (resp.defaultMode) {
                defaultMode = resp.defaultMode as ModePath;
            }

            dispatch(addedProjectModes({
                modes: {
                    // runOptimization: runOptimization ?? false,
                    biDashboard: biDashboard ?? false,
                    sharedMobilityEditor: sharedMobilityEditor ?? false,
                    networkEditor: networkEditor ?? false,
                    transitScheduleEditor: transitScheduleEditor ?? false,
                    advancedConfig: advancedConfig ?? false,
                    runs: runs ?? false,
                    evInfrastructureEditor: evInfrastructureEditor ?? false,
                    scenarioPresetEditor: scenarioPresetEditor ?? false,
                    dvInfrastructureEditor: dvInfrastructureEditor ?? false,
                    gisLayers: gisLayers ?? false,
                    gisV2Layers: gisV2Layers ?? false,
                    projectPage: projectPage ?? false,
                },
                ...(defaultMode ? {defaultMode: defaultMode as keyof AllowedProjectModes} : {})
            }));
            setError(null);
        } else {
            setError(resp);
            addToast(t('project.allowed-modes-download-error'), 'error');
        }

        const _mode = match?.params?.mode;
        checkIfAllowedAndSetMode({mode: _mode!, defaultMode, availableModes: resp as AllowedProjectModes});

        setProjectModesLoaded(true);
    }, [match?.params?.mode, checkIfAllowedAndSetMode])

    useEffect(() => {
        checkIfAllowedAndSetMode({
            mode: match?.params?.mode,
            defaultMode: defaultMode as ModePath,
            availableModes: allowedModes
        });
    }, [match?.params?.mode, defaultMode, allowedModes, checkIfAllowedAndSetMode]);

    useEffect(() => {
        return () => {
            dispatch(addedProjectModes());
        }
    }, []);

    return {
        projectModesLoaded,
        setProjectModesLoaded,
        modesError,
        allowedModes,
        mode,
        isModeAllowed,
        loadProjectModes: loadAllowedModes
    }
}